<template>
  <v-container class="pa-0">
    <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
      <v-form @submit.prevent="handleSubmit(save)" @keydown.enter.prevent>
        <v-row>
          <!-- Left Column: Main Info -->
          <v-col cols="12" md="7">
            <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-4">Información General</div>
            
            <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
              <div class="caption grey--text font-weight-black mb-1 ml-1">Nombre del Artículo</div>
              <v-text-field
                v-model="article.name"
                placeholder="Ej: Rodamiento SKF 6205"
                outlined dense
                class="modern-input mb-4"
                :error-messages="errors"
                :loading="loading"
              />
            </ValidationProvider>

            <v-row dense>
              <v-col cols="12" md="6">
                <ValidationProvider name="Código" rules="required" v-slot="{ errors }">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">SKU / Código</div>
                  <v-text-field
                    v-model="article.sku"
                    placeholder="SKU-XXXXX"
                    outlined dense
                    class="modern-input"
                    :error-messages="errors"
                    :loading="loading"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12" md="6">
                <ValidationProvider name="Familia" rules="required" v-slot="{ errors }">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Grupo / Familia</div>
                  <v-select
                    v-model="article.family"
                    :items="families"
                    item-text="name"
                    item-value="id"
                    placeholder="Seleccione..."
                    outlined dense
                    class="modern-input"
                    :error-messages="errors"
                    :loading="loading"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <div class="caption grey--text font-weight-black text-uppercase tracking-widest mt-8 mb-4">Unidades y Conversión</div>
            <v-row dense>
              <v-col cols="12" md="6">
                <ValidationProvider name="Unidad de Compra" rules="required" v-slot="{ errors }">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Unidad de Compra</div>
                  <v-select
                    v-model="article.unit"
                    :items="units"
                    item-text="name"
                    item-value="id"
                    placeholder="Unidad base"
                    outlined dense
                    class="modern-input"
                    :error-messages="errors"
                    :loading="loading"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12" md="6">
                <div class="caption grey--text font-weight-black mb-1 ml-1">Unidad de Consumo</div>
                <v-select
                  v-model="article.unit_consume"
                  :items="units"
                  item-text="name"
                  item-value="id"
                  placeholder="Opcional..."
                  outlined dense clearable
                  class="modern-input"
                  :loading="loading"
                />
              </v-col>
            </v-row>

            <v-slide-y-transition>
              <v-row dense v-if="article.unit_consume">
                <v-col cols="12">
                  <ValidationProvider name="Factor de Conversión" rules="required|minvalue:0" v-slot="{ errors }">
                    <div class="caption primary--text font-weight-black mb-1 ml-1">Factor de Conversión (1 Compra = X Consumo)</div>
                    <v-text-field
                      v-model.number="article.unit_consume_factor"
                      type="number"
                      placeholder="1.0"
                      outlined dense
                      class="modern-input"
                      :error-messages="errors"
                      :loading="loading"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-slide-y-transition>
          </v-col>

          <!-- Right Column: Config & Alerts -->
          <v-col cols="12" md="5">
            <v-card flat class="pa-6 bg-lightgrey rounded-xl border-dashed-secondary fill-height">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Configuración de Inventario</div>
              
              <div class="d-flex align-center mb-6">
                <div class="mr-4">
                  <div class="caption grey--text font-weight-black mb-1">Stock Crítico</div>
                  <v-text-field
                    v-model.number="article.critic"
                    type="number"
                    outlined dense hide-details
                    class="modern-input-minimal"
                    style="width: 100px"
                    :min="0"
                  />
                </div>
                <v-spacer></v-spacer>
                <div class="text-right">
                  <div class="caption grey--text font-weight-black mb-1">Alertas Activas</div>
                  <v-switch
                    v-model="article.alert"
                    hide-details
                    dense
                    class="mt-0 pt-0 modern-switch"
                    color="primary"
                  ></v-switch>
                </div>
              </div>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Observaciones / Notas</div>
              <v-textarea
                v-model="article.notes"
                placeholder="Notas técnicas o descriptivas..."
                outlined
                rows="4"
                hide-details
                class="modern-input bg-white"
              ></v-textarea>

              <v-divider class="my-8 border-dashed"></v-divider>

              <div class="text-center pt-2">
                <v-btn
                  block
                  color="success"
                  class="font-weight-black shadow-sm h-52"
                  type="submit"
                  :loading="saving"
                  :disabled="loading"
                >
                  <v-icon left>fas fa-save</v-icon>
                  {{ id > -1 ? 'Actualizar Artículo' : 'Guardar Nuevo Artículo' }}
                </v-btn>
                <div class="mt-4">
                  <v-btn text color="grey darken-1" class="caption font-weight-black uppercase" @click="$emit('close')">
                    Cancelar
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </ValidationObserver>
  </v-container>
</template>

<script>
import { getArticle, createArticle, updateArticle } from '@/api/stocks'
import notifications from '@/utils/notifications'

export default {
  name: 'ArticleCreateUpdate',
  props: {
    id: { type: Number, required: true },
    families: { type: Array, default: () => [] },
    units: { type: Array, default: () => [] }
  },
  data() {
    return {
      article: {
        id: -1,
        sku: '',
        name: '',
        family: '',
        unit: '',
        unit_consume: '',
        unit_consume_factor: 1,
        critic: 0,
        alert: false,
        notes: ''
      },
      loading: false,
      saving: false,
    }
  },
  methods: {
    async save() {
      this.saving = true
      const article_data = {
        sku: this.article.sku,
        name: this.article.name,
        family_id: this.article.family,
        unit_id: this.article.unit,
        unit_consume_factor: this.article.unit_consume_factor || 1,
        critic: this.article.critic || 0,
        alert: this.article.alert,
        notes: this.article.notes,
      }
      
      if (this.article.unit_consume) 
        article_data.unit_consume_id = this.article.unit_consume

      try {
        if (this.id > -1) {
          await updateArticle(this.id, article_data)
          notifications.showSuccess(`${this.article.name} actualizado correctamente`)
        } else {
          await createArticle(article_data)
          notifications.showSuccess(`${this.article.name} creado correctamente`)
        }
        this.$emit('updated')
        this.$emit('close')
      } catch (err) {
        notifications.showError('Error al procesar el artículo - Verifique si el SKU ya existe')
        console.error(err)
      } finally {
        this.saving = false
      }
    },
    async get() {
      if (this.id > -1) {
        this.loading = true
        try {
          const res = await getArticle(this.id)
          // Mapeo defensivo para soportar tanto objetos anidados como IDs planos
          this.article = {
            ...res,
            family: res.family && typeof res.family === 'object' ? res.family.id : res.family,
            unit: res.unit && typeof res.unit === 'object' ? res.unit.id : res.unit,
            unit_consume: res.unit_consume && typeof res.unit_consume === 'object' ? res.unit_consume.id : res.unit_consume
          }
        } catch (err) {
          notifications.showError('Error al cargar datos del artículo')
        } finally {
          this.loading = false
        }
      } else {
        this.article = {
          id: -1,
          sku: '',
          name: '',
          family: '',
          unit: '',
          unit_consume: '',
          unit_consume_factor: 1,
          critic: 0,
          alert: false,
          notes: ''
        }
      }
    }
  },
  watch: {
    id: { handler: 'get', immediate: true }
  }
}
</script>

<style scoped>
.bg-lightgrey { background-color: #f8fbfc; }
.border-dashed-secondary { border: 1px dashed #e0e4e8 !important; }
.border-dashed { border: 1px dashed #ced4da !important; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.tracking-widest { letter-spacing: 1.8px; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important; }
.h-52 { height: 52px !important; }

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-input >>> .v-input__control { background-color: white !important; }
.modern-input >>> input { font-size: 0.9rem !important; font-weight: 500 !important; color: #1a1f2e !important; }

.modern-input-minimal >>> .v-input__control fieldset { border-radius: 6px !important; border-color: #ced4da !important; }
.modern-input-minimal >>> input { font-size: 0.85rem !important; text-align: center !important; }

.v-btn.success { background-color: #4CAF50 !important; }
</style>